import { shallowEqual } from "react-redux";
import {
    getPreviewScores,
    scorableSliceActions,
    selectIsScorableComplete,
    selectNpsTextAnswer,
    selectQuestionScore,
} from "features/scorable/scorableSlice";
import "./NPSQuestionOptions.less";
import {
    GuxFormFieldTextarea,
    GuxTruncate,
} from "genesys-spark-components-react";

import {
    selectMaxResponseCharacters,
    selectNpsPrompt,
    selectQuestionGroupIdContainingQuestion,
} from "features/qualityForm/qualityFormSlice";
import { selectQuestion } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { ReadOnlyRadio } from "features/common/ReadOnlyInput";
import { useStrings } from "features/i18n/useStrings";
import strings from "./NpsQuestionOptions.strings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { RadialButton } from "features/common/RadialButton";
import { PreviewModeContext, ScorableValidationContext } from "../Contexts";
import { useContext } from "react";
import { MarkdownInline } from "features/common/Markdown";
import { selectIsNPSAnswerValid } from "features/scorable/ScorableFormValidations";
import { RemainingCharacters } from "../RemainingCharacters";
const npsScores: Array<number> = Array.from(Array(11).keys());

export default function NpsQuestionOptions({
    questionId,
    disabled = true,
}: {
    questionId: string;
    disabled: boolean;
}) {
    const t = useStrings(strings);
    const npsScore = useAppSelector((state) => {
        return selectQuestionScore(state, questionId)?.npsScore;
    });
    const dispatch = useAppDispatch();
    const { previewMode } = useContext(PreviewModeContext);
    const questionGroupId = useAppSelector(
        (state) =>
            selectQuestionGroupIdContainingQuestion(questionId)(
                state.qualityForm
            ) ?? ""
    );
    return (
        <div className="NpsQuestionOptions">
            <div className="nps-answer-map">
                {npsScores.map((value) => {
                    const selected = value === npsScore;
                    return (
                        <div className="nps-score-option" key={value}>
                            {disabled ? (
                                <ReadOnlyRadio
                                    checked={selected}
                                    label={value.toString()}
                                />
                            ) : (
                                <>
                                    <RadialButton
                                        selected={value === npsScore}
                                        value={value.toString()}
                                        text={value.toString()}
                                        onChange={({ target }) => {
                                            dispatch(
                                                scorableSliceActions.setNpsScore(
                                                    {
                                                        questionGroupId,
                                                        questionId,
                                                        value: Number(
                                                            target.defaultValue
                                                        ),
                                                    }
                                                )
                                            );
                                            if (previewMode) {
                                                dispatch(
                                                    getPreviewScores({
                                                        isScorableFinished:
                                                            false,
                                                    })
                                                );
                                            }
                                        }}
                                        verticalAlign={true}
                                    />
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="nps-range-text">
                <span className="low-score-label">{t.labels.lowNpsScore}</span>
                <span className="high-score-label">
                    {t.labels.highNpsScore}
                </span>
            </div>
        </div>
    );
}

export function NpsQuestionPrompt({
    questionId,
    readOnlyMode = false,
}: {
    questionId: string;
    readOnlyMode?: boolean;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);
    const { showValidationErrors } = useContext(ScorableValidationContext);
    const { explanationPrompt, hasDefinedExplanationPrompt } = useAppSelector(
        (state) => {
            const explanationPrompt = selectNpsPrompt(state, questionId);
            return {
                explanationPrompt: explanationPrompt,
                hasDefinedExplanationPrompt: explanationPrompt !== undefined,
            };
        },
        shallowEqual
    );
    const maxResponseCharacters = useAppSelector((state) => {
        return selectMaxResponseCharacters(state, questionId);
    });
    const npsTextAnswer = useAppSelector((state) => {
        return selectNpsTextAnswer(questionId)(state) ?? "";
    });
    const naEnabled = useAppSelector((state) => {
        return selectQuestion(state, questionId)?.naEnabled ?? false;
    });
    const isScorableComplete = useAppSelector(selectIsScorableComplete);

    const isFollowUpExplanationRequired = !naEnabled && !isScorableComplete;

    const isNPSAnswerInvalid = useAppSelector(
        (state) =>
            showValidationErrors && !selectIsNPSAnswerValid(state, questionId)
    );

    return (
        <>
            {hasDefinedExplanationPrompt && (
                <div
                    className="NpsQuestionPrompt"
                    data-invalid={isNPSAnswerInvalid === true}
                >
                    {readOnlyMode && !isScorableComplete ? (
                        <div
                            className={`explanation-prompt-read-only-label ${
                                !naEnabled ? "required" : ""
                            }`}
                        >
                            <div className="explanation-prompt-read-only-text">
                                <GuxTruncate max-lines={15}>
                                    <MarkdownInline
                                        markdownInlineText={
                                            explanationPrompt ?? ""
                                        }
                                    ></MarkdownInline>
                                </GuxTruncate>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="explanation-prompt-label">
                                <GuxTruncate max-lines={15}>
                                    <div
                                        className={`${
                                            isFollowUpExplanationRequired
                                                ? " required"
                                                : ""
                                        }`}
                                    >
                                        <MarkdownInline
                                            markdownInlineText={
                                                explanationPrompt ?? ""
                                            }
                                        ></MarkdownInline>
                                    </div>
                                </GuxTruncate>
                            </div>

                            <GuxFormFieldTextarea
                                resize="auto"
                                screenreader-text={t.labels.followUpExplanation}
                                label-position="screenreader"
                            >
                                <textarea
                                    slot="input"
                                    required={!naEnabled && !isScorableComplete}
                                    readOnly={isScorableComplete}
                                    tabIndex={isScorableComplete ? -1 : 0}
                                    value={npsTextAnswer}
                                    onChange={({ currentTarget }) => {
                                        dispatch(
                                            scorableSliceActions.setNpsTextAnswer(
                                                {
                                                    questionId: questionId,
                                                    text: currentTarget.value,
                                                }
                                            )
                                        );
                                    }}
                                ></textarea>
                                <label slot="label"></label>
                                {npsTextAnswer.length >
                                    maxResponseCharacters && (
                                    <span slot="error" role="alert">
                                        {t.labels.error({
                                            total: maxResponseCharacters,
                                        })}
                                    </span>
                                )}
                            </GuxFormFieldTextarea>
                        </>
                    )}
                    {!readOnlyMode && (
                        <RemainingCharacters
                            currentCount={npsTextAnswer.length}
                            totalCount={maxResponseCharacters}
                        />
                    )}
                </div>
            )}
        </>
    );
}
